import React, { useMemo } from 'react';
import { formatCurrency, formatPercentWithSign, getValueColorClass } from '../../utils/contractCalculations';
import { isStockRow, positionValues } from './portfolioConstants';

/**
 * Totals strip above the holdings grid.
 *
 * This is the detail a portfolio earns over a watchlist group: a watchlist
 * answers "how would these behave?", a portfolio also has to answer "what am I
 * actually holding, and am I up or down?". Everything here is derived from the
 * saved contracts already in memory — no extra API call.
 */
export default function PortfolioSummary({ contracts = [] }) {
  const totals = useMemo(() => {
    let costBasis = 0;
    let marketValue = 0;
    let pnl = 0;
    let optionCount = 0;
    let stockCount = 0;
    let optionValue = 0;
    let stockValue = 0;

    contracts.forEach((contract) => {
      const values = positionValues(contract);
      costBasis += values.costBasis;
      marketValue += values.marketValue;
      pnl += values.pnl;

      if (isStockRow(contract)) {
        stockCount += 1;
        stockValue += values.marketValue;
      } else {
        optionCount += 1;
        optionValue += values.marketValue;
      }
    });

    return {
      costBasis,
      marketValue,
      pnl,
      pnlPercent: costBasis > 0 ? (pnl / costBasis) * 100 : 0,
      optionCount,
      stockCount,
      optionValue,
      stockValue,
    };
  }, [contracts]);

  if (!contracts || contracts.length === 0) return null;

  const stats = [
    {
      label: 'Cost Basis',
      value: formatCurrency(totals.costBasis),
      tooltip: 'Total you paid across every position: units x avg cost (x100 for option contracts).',
    },
    {
      label: 'Market Value',
      value: formatCurrency(totals.marketValue),
      tooltip: "Total current value of every position at the latest refreshed prices.",
    },
    {
      label: 'Unrealized P&L',
      value: formatCurrency(totals.pnl),
      sub: formatPercentWithSign(totals.pnlPercent, 2),
      colorClass: getValueColorClass(totals.pnl),
      tooltip: 'Market value minus cost basis. Short positions are inverted, so a positive number always means a gain.',
    },
    {
      label: 'Positions',
      value: `${contracts.length}`,
      sub: `${totals.optionCount} option${totals.optionCount === 1 ? '' : 's'} • ${totals.stockCount} stock`,
      tooltip: 'How your holdings split between option contracts and plain stock.',
    },
  ];

  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4"
      data-tour="portfolio-summary"
    >
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="pf-tile"
          data-tooltip-id="portfolio-tooltip"
          data-tooltip-content={stat.tooltip}
        >
          <div className="pf-tile__label">{stat.label}</div>
          <div className={`pf-tile__value ${stat.colorClass || 'text-brand-white'}`}>
            {stat.value}
          </div>
          {stat.sub && (
            <div className={`pf-tile__sub ${stat.colorClass || 'text-[color:var(--pf-muted)]'}`}>
              {stat.sub}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
